import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Lock, Loader2, AlertTriangle, Eye } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

const ROLE_LABEL = { admin: 'Admin', analyst: 'Analyst', ac_member: 'AC-Mitglied' };

// OAuth consent page for the Claude Connector: Claude sends the user here (via /api/mcp-auth/authorize);
// the logged-in OLTA user allows or denies read access, then is redirected back to Claude.
export default function ClaudeConnectPage() {
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = Object.fromEntries(params.entries());

  useEffect(() => {
    api.mcpAuthorizeContext(q).then(r => setCtx(r.data)).catch(e => setError(e.response?.data?.detail || 'Anfrage konnte nicht geprüft werden'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api.mcpAuthorizeDecision({ ...q, approve });
      window.location.href = r.data.redirect_to;
    } catch (e) {
      setError(e.response?.data?.detail || 'Freigabe fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="claude-connect-page">
      <div className="w-full max-w-md bg-card border border-border rounded-sm p-6 sm:p-8 space-y-5">
        <div className="flex items-center gap-3">
          <img src="/olta-logo.png" alt="OLTA" className="h-8 w-auto" />
          <span className="text-muted-foreground">×</span>
          <span className="font-heading text-lg text-foreground">{ctx?.client_name || 'Claude'}</span>
        </div>
        <div>
          <h1 className="font-heading text-xl text-foreground">{ctx?.client_name || 'Claude'} möchte auf OLTA zugreifen</h1>
          <p className="text-xs text-muted-foreground mt-1">Nur lesend — Claude kann nichts anlegen, ändern oder löschen.</p>
        </div>

        {!ctx && !error && <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="claude-connect-loading"><Loader2 className="w-4 h-4 animate-spin" /> Anfrage wird geprüft…</div>}

        {(error || (ctx && !ctx.valid)) && (
          <div className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 rounded-sm p-3 text-xs text-red-300" data-testid="claude-connect-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error || ctx.error}</span>
          </div>
        )}

        {ctx?.valid && (
          <>
            <div className="border border-border rounded-sm p-3 space-y-2 text-sm" data-testid="claude-connect-user">
              <p className="text-foreground">Angemeldet als <span className="font-medium">{ctx.user?.name || user?.name}</span>
                <span className="text-muted-foreground"> · {ROLE_LABEL[ctx.user?.role] || ctx.user?.role}</span></p>
              <p className="text-[11px] text-muted-foreground">{ctx.user?.email}</p>
              <button onClick={logout} className="text-[11px] text-sky-300 hover:underline" data-testid="claude-connect-switch-user">Anderes Konto verwenden</button>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1.5" data-testid="claude-connect-scope">
              <li className="flex items-start gap-2"><Eye className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" /> {ctx.scope_label}</li>
              <li className="flex items-start gap-2"><Lock className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" /> Keine Schreibrechte. Die Verbindung kann jederzeit in OLTA (Team & Roles → Claude-Connector) getrennt werden.</li>
            </ul>
            {!ctx.allowed && (
              <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-xs text-amber-200" data-testid="claude-connect-not-allowed">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Dein Admin hat den Claude-Zugang für dich noch nicht freigeschaltet. Bitte in OLTA nachfragen (Team & Roles → Claude-Connector).</span>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={() => decide(false)} disabled={busy} className="rounded-sm text-xs h-9" data-testid="claude-connect-deny-btn">Ablehnen</Button>
              <Button onClick={() => decide(true)} disabled={busy || !ctx.allowed} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9 px-4" data-testid="claude-connect-allow-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />} Zugriff erlauben
              </Button>
            </div>
          </>
        )}
        <p className="text-[10px] text-muted-foreground/70">Du wirst danach zu claude.ai zurückgeleitet.</p>
      </div>
    </div>
  );
}
